import { useContext } from 'react';

import { OptimizelyDecideOption, useDecision } from '@optimizely/react-sdk';

import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { FLAG } from 'Utilities/analytics/optimizely/constants';
import { isUndefined } from 'Utilities/helpers';
import { useFeatureFlags } from 'Utilities/hooks';

import { APIResponseStatus } from 'Services/api.types';

import { useSaleDetailsSub } from '../vehicleCollection/hooks/useSaleDetailsSub';
import { FAMILY_KEY, SELLER } from '../vehicleProfiling/VehicleProfiling.constants';

import { useIncodeVerificationDocuments } from './uploadDocuments/pages/IncodeSDKPage/hooks/useIncodeVerificationDocuments';
import { DocumentCaptureConfirmationProgressContext, DocumentCaptureOfferContext } from './DocumentCaptureContexts';

const DISABLE_DECISION = [OptimizelyDecideOption.DISABLE_DECISION_EVENT];

export const useDocumentCaptureExperimentDecisions = () => {
  const { incodeEnabled } = useFeatureFlags();
  const { userState: user } = useContext(UserContext);
  const { data } = useContext(DocumentCaptureOfferContext) || {};

  const {
    progress: {
      bankDetailsAdded,
      bankDetailsReviewFailed,
      documents,
    },
  } = useContext(DocumentCaptureConfirmationProgressContext) ?? {};
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);

  const { hasNegativeEquity, offerStatus } = useSaleDetailsSub();

  const hasSomeDocs = documents?.some(({ uploaded }) => (!!uploaded));
  const hasValidBankDetails = bankDetailsAdded === true && !bankDetailsReviewFailed;
  const isDataLoaded = !isUndefined(data);

  const keeperType = data?.seller?.keeperType;
  const isSellerKeeper = keeperType === SELLER;
  const isFamilyOrFriendKeeper = keeperType === FAMILY_KEY;

  const shouldFireKeeperAuthDecision = isFamilyOrFriendKeeper && isDataLoaded;
  const [
    { enabled: isKeeperAuthFlagEnabled, variationKey: keeperAuthVariationKey },
    keeperAuthClientReady,
  ] = useDecision(
    FLAG.INCODE_KEEPER_AUTHORIZATION,
    { decideOptions: shouldFireKeeperAuthDecision ? [] : DISABLE_DECISION },
    { overrideUserId: user?.dpSellerId?.toString() ?? '' },
  );

  const inKeeperAuthExperiment = keeperAuthClientReady
    && isKeeperAuthFlagEnabled
    && keeperAuthVariationKey === 'on'
    && isFamilyOrFriendKeeper;
  const isKeeperTypeAllowed = isSellerKeeper || inKeeperAuthExperiment;

  const payload = {
    enquiryId: vehicle?.enquiry?.dpEnquiryId,
  };

  const {
    hasActiveIncodeSession,
    isLoading: isLoadingIncodeSession,
  } = useIncodeVerificationDocuments({
    authToken: user?.auth_token,
    payload,

  });

  if (isUndefined(data) || isUndefined(documents) || offerStatus !== APIResponseStatus.SUCCESS) {
    return {
      isDecisionLoading: isLoadingIncodeSession || offerStatus === APIResponseStatus.LOADING,
      isIncodeEligible: false,
      isMandatoryBankDetailsEligible: false,
    };
  }

  const isIncodeEligible = (hasActiveIncodeSession && incodeEnabled)
  || (incodeEnabled && isKeeperTypeAllowed && !hasSomeDocs && hasValidBankDetails);

  const isMandatoryBankDetailsEligible = [
    !hasSomeDocs,
    !hasValidBankDetails,
    !hasNegativeEquity,
  ].every(Boolean);

  return {
    isDecisionLoading: isLoadingIncodeSession,
    isIncodeEligible,
    isMandatoryBankDetailsEligible,
  };
};
